/**
 * Privacy notice / 隐私声明 / Integritetsinformation.
 * Written to cover PIPL Art. 17 & 39 (what, why, how, who receives it abroad, rights, contact) and
 * GDPR Art. 13. Facts (controller, location, processor) come from the server so the text stays true.
 * Bump NOTICE_TEXT_VERSION on the server when this wording changes materially (last: 2026-10-23b, voice + notifications).
 */
import { useState } from 'react';
import type { PrivacyInfoDTO } from '@tc/contracts';

type Lang = 'sv-SE' | 'zh-Hans' | 'en';
const asLang = (l: string): Lang => (l === 'sv-SE' || l === 'zh-Hans' ? l : 'en');

const LOC: Record<string, Record<Lang, string>> = {
  'EU (Sweden)': { 'sv-SE': 'EU (Sverige)', 'zh-Hans': '欧盟（瑞典）', en: 'EU (Sweden)' },
  Singapore: { 'sv-SE': 'Singapore', 'zh-Hans': '新加坡', en: 'Singapore' },
  USA: { 'sv-SE': 'USA', 'zh-Hans': '美国', en: 'USA' },
  Japan: { 'sv-SE': 'Japan', 'zh-Hans': '日本', en: 'Japan' },
  Germany: { 'sv-SE': 'Tyskland', 'zh-Hans': '德国', en: 'Germany' },
  'Hong Kong': { 'sv-SE': 'Hongkong', 'zh-Hans': '中国香港', en: 'Hong Kong' },
};
const place = (p: string, lang: Lang) => LOC[p]?.[lang] ?? p;

export function consentLabels(info: PrivacyInfoDTO, uiLocale: string): { processing: string; crossBorder: string; title: string; read: string } {
  const lang = asLang(uiLocale);
  const where = place(info.dataLocation, lang);
  const proc = info.processor ? `${info.processor.name} (${place(info.processor.location, lang)})` : null;
  if (lang === 'zh-Hans')
    return {
      title: '隐私声明',
      read: '阅读隐私声明',
      processing: `我已阅读《隐私声明》，同意 ${info.controller} 按其所述处理我的个人信息。`,
      crossBorder: proc
        ? `我单独同意：我的个人信息和消息存储在中国大陆境外的服务器（${where}），消息文字将发送至 ${proc} 进行翻译。`
        : `我单独同意：我的个人信息和消息存储在中国大陆境外的服务器（${where}）。`,
    };
  if (lang === 'sv-SE')
    return {
      title: 'Integritetsinformation',
      read: 'Läs integritetsinformationen',
      processing: `Jag har läst integritetsinformationen och godkänner att ${info.controller} behandlar mina personuppgifter enligt den.`,
      crossBorder: proc
        ? `Jag godkänner separat att mina uppgifter och meddelanden lagras på en server utanför Kina (${where}) och att meddelandetext skickas till ${proc} för översättning.`
        : `Jag godkänner separat att mina uppgifter och meddelanden lagras på en server utanför Kina (${where}).`,
    };
  return {
    title: 'Privacy notice',
    read: 'Read the privacy notice',
    processing: `I have read the privacy notice and agree that ${info.controller} processes my personal information as described.`,
    crossBorder: proc
      ? `I separately agree that my information and messages are stored on a server outside mainland China (${where}) and that message text is sent to ${proc} for translation.`
      : `I separately agree that my information and messages are stored on a server outside mainland China (${where}).`,
  };
}

export function PrivacyNoticeText({ info, uiLocale }: { info: PrivacyInfoDTO; uiLocale: string }) {
  const lang = asLang(uiLocale);
  const where = place(info.dataLocation, lang);
  const proc = info.processor ? `${info.processor.name}, ${place(info.processor.location, lang)}` : null;
  const c = info.controller;
  const mail = <a href={`mailto:${info.contactEmail}`}>{info.contactEmail}</a>;

  if (lang === 'zh-Hans')
    return (
      <div className="notice" lang="zh-Hans">
        <h4>谁处理您的信息</h4>
        <p>{c}（以下简称“我们”）运营本聊天工具，用于我们公司与受邀供应商之间的业务沟通。联系方式：{mail}</p>
        <h4>收集哪些信息</h4>
        <ul>
          <li>账号信息：姓名、电子邮箱、所属公司、界面语言和阅读语言、加密存储的密码。</li>
          <li>您发送的内容：消息、表情回应、编辑记录，以及系统生成的译文。</li>
          <li>语音：您选择随消息发送的录音。用于听写的录音只用于转换成文字，不会保存。</li>
          <li>技术信息：登录会话、设备类型（浏览器信息），以及安全日志（如登录失败、管理操作），不包含聊天内容。</li>
        </ul>
        <h4>用途</h4>
        <p>仅用于：让您与我们沟通、将消息翻译成每位读者的语言、保障账号安全。不用于广告，不出售。</p>
        <h4>存储地点与跨境提供</h4>
        <p>
          所有数据存储在中国大陆境外的服务器上（{where}）。
          {proc ? (
            <> 为了翻译，消息文字（不含您的密码或其他账号信息）会发送给翻译服务提供方 {proc}。{info.speech && '使用语音输入时，录音也会发送给该服务商进行语音识别。'}{info.images && '当有人要求识别并翻译图片中的文字时，该图片也会发送给该服务商。'}{info.readAloud && '点击"朗读"时，中文和英文消息文字会发送给该服务商以生成自然语音。'}{info.swedishVoiceProcessor && `朗读瑞典语消息时，消息文字会发送给 ${info.swedishVoiceProcessor.name}（${info.swedishVoiceProcessor.location}）以生成语音；该服务商同样不将客户内容用于训练模型。`}该服务商的条款规定不将客户内容用于训练模型，并可能为防止滥用短期保留数据（通常不超过 30 天）。</>
          ) : (
            <> 当前不使用任何外部翻译服务。</>
          )}
        </p>
        <h4>通知</h4>
        <p>如果您开启推送通知，我们会通过您的浏览器或手机系统的推送服务（如 Apple、Google、Mozilla）发送提醒，只包含发送人和聊天名称，不包含消息内容。若有消息长时间未读，我们会发送电子邮件提醒（不含消息内容），您可以在"设置"中关闭。</p>
        <h4>谁能看到</h4>
        <p>只有您所在聊天的成员能看到该聊天中的消息，以及您读到了哪条消息（已读回执）、您是否在线和正在输入。管理员可以管理账号，但无法阅读他人的聊天。</p>
        <h4>保存期限</h4>
        <p>您的账号存续期间保存。您删除的消息会被清除内容（包括录音）。删除账号后，您发送的所有消息和录音都会被清除，账号信息被匿名化；仅保留证明您曾同意本声明的记录和不含内容的安全日志。</p>
        <h4>您的权利</h4>
        <p>您可以在“设置”中查看并修改个人资料、下载您的数据副本、删除消息或删除整个账号（即撤回同意）。如有其他请求，请联系 {mail}。</p>
        <p className="muted small">版本 {info.version}</p>
      </div>
    );

  if (lang === 'sv-SE')
    return (
      <div className="notice" lang="sv">
        <h4>Vem behandlar dina uppgifter</h4>
        <p>{c} (”vi”) driver det här chattverktyget för affärskommunikation mellan vårt företag och inbjudna leverantörer. Kontakt: {mail}</p>
        <h4>Vilka uppgifter</h4>
        <ul>
          <li>Kontouppgifter: namn, e-post, företag, språk för appen och för läsning, lösenord (krypterat).</li>
          <li>Det du skickar: meddelanden, reaktioner, redigeringar och de översättningar som skapas.</li>
          <li>Röst: inspelningar som du väljer att skicka med ett meddelande. Inspelningar för diktering görs bara om till text och sparas inte.</li>
          <li>Teknik: inloggningssessioner, enhetstyp (webbläsarinformation) och säkerhetsloggar (t.ex. misslyckade inloggningar, administrativa åtgärder) – aldrig chattinnehåll.</li>
        </ul>
        <h4>Varför</h4>
        <p>Enbart för att du ska kunna kommunicera med oss, för att översätta meddelanden till varje läsares språk och för att skydda kontot. Ingen reklam, ingen försäljning av uppgifter.</p>
        <h4>Var lagras de, och vem får dem</h4>
        <p>
          All data lagras på en server utanför Kina ({where}).
          {proc ? (
            <> För översättning skickas meddelandetexten (aldrig lösenord eller övriga kontouppgifter) till översättningstjänsten {proc}.{info.speech && ' När du använder röst skickas även inspelningen dit för taligenkänning.'}{info.images && ' När någon ber om att texten i en bild ska läsas och översättas skickas även bilden dit.'}{info.readAloud && ' När du trycker på Läs upp skickas kinesisk och engelsk meddelandetext dit för att skapa en naturlig röst.'}{info.swedishVoiceProcessor && ` När svensk text läses upp skickas meddelandetexten till ${info.swedishVoiceProcessor.name} (${info.swedishVoiceProcessor.location}) för att skapa rösten; inte heller där används kundinnehåll för att träna modeller.`} Enligt tjänstens villkor används kundinnehåll inte för att träna modeller, men kan sparas kort (vanligen högst 30 dagar) för att förhindra missbruk.</>
          ) : (
            <> Just nu används ingen extern översättningstjänst.</>
          )}
        </p>
        <h4>Aviseringar</h4>
        <p>Slår du på pushnotiser skickas de via webbläsarens eller telefonens pushtjänst (t.ex. Apple, Google, Mozilla) och innehåller bara avsändare och chattnamn – aldrig meddelandetexten. Ligger meddelanden olästa en längre tid skickar vi en påminnelse via e-post, utan meddelandeinnehåll. Det kan du stänga av under Inställningar.</p>
        <h4>Vem kan se vad</h4>
        <p>Bara medlemmarna i en chatt ser dess meddelanden och hur långt du har läst (läskvitton), om du är aktiv och om du skriver. Administratörer hanterar konton men kan inte läsa andras chattar.</p>
        <h4>Hur länge</h4>
        <p>Så länge ditt konto finns. Meddelanden du raderar töms på innehåll, inklusive inspelningar. Raderar du kontot töms alla dina meddelanden och inspelningar och kontot anonymiseras; kvar finns bara beviset på att du samtyckte och säkerhetsloggar utan innehåll.</p>
        <h4>Dina rättigheter</h4>
        <p>Under Inställningar kan du se och ändra din profil, ladda ner en kopia av dina uppgifter, radera meddelanden eller hela kontot (vilket återkallar samtycket). Andra frågor: {mail}.</p>
        <p className="muted small">Version {info.version}</p>
      </div>
    );

  return (
    <div className="notice" lang="en">
      <h4>Who processes your information</h4>
      <p>{c} (“we”) runs this chat tool for business communication between our company and invited suppliers. Contact: {mail}</p>
      <h4>What we collect</h4>
      <ul>
        <li>Account: name, e-mail, company, app and reading language, password (stored hashed).</li>
        <li>What you send: messages, reactions, edits and the translations made of them.</li>
        <li>Voice: recordings you choose to send with a message. Recordings for dictation are only turned into text and are not kept.</li>
        <li>Technical: sign-in sessions, device type (browser information) and security logs (e.g. failed sign-ins, admin actions) – never chat content.</li>
      </ul>
      <h4>Why</h4>
      <p>Only so you can communicate with us, to translate messages into each reader’s language, and to keep accounts secure. No advertising, no selling of data.</p>
      <h4>Where it is stored and who receives it</h4>
      <p>
        All data is stored on a server outside mainland China ({where}).
        {proc ? (
          <> For translation, message text (never passwords or other account details) is sent to the translation service {proc}.{info.speech && ' When you use voice, the recording is also sent there for speech recognition.'}{info.images && ' When someone asks to read and translate the text in an image, the image is also sent there.'}{info.readAloud && ' When you tap Read aloud, Chinese and English message text is sent there to create a natural voice.'}{info.swedishVoiceProcessor && ` When Swedish text is read aloud, the message text is sent to ${info.swedishVoiceProcessor.name} (${info.swedishVoiceProcessor.location}) to create the voice; it too does not use customer content to train models.`} Under its terms customer content is not used to train models, but may be kept briefly (usually up to 30 days) to prevent abuse.</>
        ) : (
          <> No external translation service is currently used.</>
        )}
      </p>
      <h4>Notifications</h4>
      <p>If you turn on push notifications, they are delivered through your browser’s or phone’s push service (e.g. Apple, Google, Mozilla) and contain only the sender and chat name – never the message text. If messages stay unread for a while we send an e-mail reminder without message content. You can turn this off in Settings.</p>
      <h4>Who can see what</h4>
      <p>Only members of a chat see its messages and how far you have read (read receipts), whether you are active and when you are typing. Administrators manage accounts but cannot read other people’s chats.</p>
      <h4>How long</h4>
      <p>As long as your account exists. Messages you delete are emptied, including recordings. If you delete your account, all your messages and recordings are emptied and the account is anonymised; only proof of your consent and security logs without content are kept.</p>
      <h4>Your rights</h4>
      <p>In Settings you can view and edit your profile, download a copy of your data, delete messages or your whole account (which withdraws consent). Other requests: {mail}.</p>
      <p className="muted small">Version {info.version}</p>
    </div>
  );
}

/** Notice + the two separate consent checkboxes (unbundled, as PIPL requires for cross-border). */
export function ConsentFields({
  info,
  uiLocale,
  value,
  onChange,
  showNoticeToggle = true,
}: {
  info: PrivacyInfoDTO;
  uiLocale: string;
  value: { processing: boolean; crossBorder: boolean };
  onChange: (v: { processing: boolean; crossBorder: boolean }) => void;
  /** Hide the "read the notice" toggle where the full notice is already shown. */
  showNoticeToggle?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const l = consentLabels(info, uiLocale);
  return (
    <fieldset className="consent">
      {showNoticeToggle && (
        <button type="button" className="link-btn" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
          {l.read}
        </button>
      )}
      {showNoticeToggle && open && <PrivacyNoticeText info={info} uiLocale={uiLocale} />}
      <label className="check">
        <input type="checkbox" required checked={value.processing} onChange={(e) => onChange({ ...value, processing: e.target.checked })} />
        <span>{l.processing}</span>
      </label>
      <label className="check">
        <input type="checkbox" required checked={value.crossBorder} onChange={(e) => onChange({ ...value, crossBorder: e.target.checked })} />
        <span>{l.crossBorder}</span>
      </label>
    </fieldset>
  );
}
